import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, FolderOpen, Edit3 } from "lucide-react";
import { useProjects } from "../hooks/useProjects";
import { useProjectsActivity } from "../hooks/useProjectsActivity";
import type { BuildingProject } from "@/types/projects";
import { ProjectActivityLine } from "@/domains/projects/components/ProjectActivityLine";
import {
  PROJECT_TILE_CLASS,
  PROJECT_TILE_DASHED_CLASS,
  PROJECT_TILE_INTERACTIVE_CLASS,
} from "@/domains/projects/components/projectTile";
import {
  ProjectCreateFieldLabel,
  ProjectStructureGuidance,
} from "@/domains/projects/components/ProjectCreateFormPresentation";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { usePermissions } from "@/hooks/usePermissions";
import { FEATURE_FLAGS, useFeatureFlag } from "@/lib/posthog";

export type ProjectSortOption =
  | "date-desc"
  | "date-asc"
  | "name-asc"
  | "name-desc"
  | "modified-desc"
  | "modified-asc"
  | "user-asc"
  | "user-desc";

interface ProjectsSectionProps {
  buildingId: string;
  selectedProject: BuildingProject | null;
  onProjectSelect: (project: BuildingProject | null) => void;
  /** Sort order for the project cards. Defaults to newest-created first. */
  sortOption?: ProjectSortOption;
  /** Show a per-card activity line (recent uploads / open issues / last
   * activity). Off by default — only the building dashboard opts in. */
  showActivity?: boolean;
}

export function ProjectsSection({
  buildingId,
  selectedProject,
  onProjectSelect,
  sortOption,
  showActivity = false,
}: ProjectsSectionProps) {
  const { t, language } = useLanguage();
  const { projects, createProject, isCreating, updateProject, isUpdating } =
    useProjects(buildingId);
  const { canCreateProjects, canModifyProjects, canViewPMTool } = usePermissions();
  const pmModuleEnabled = useFeatureFlag(FEATURE_FLAGS.PM_MODULE_ENABLED, true);
  const {
    data: activity,
    isSuccess: activityLoaded,
    isError: activityError,
  } = useProjectsActivity(buildingId, {
    enabled: showActivity,
    includeIssues: pmModuleEnabled && canViewPMTool,
  });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<BuildingProject | null>(null);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDescription, setNewProjectDescription] = useState("");
  const createFieldId = useId();
  const createNameId = `${createFieldId}-project-name`;
  const createDescriptionId = `${createFieldId}-project-description`;

  const handleCreateProject = () => {
    if (!newProjectName.trim()) return;

    createProject({
      building_id: buildingId,
      name: newProjectName.trim(),
      description: newProjectDescription.trim() || undefined,
      source: "documents",
    });

    setNewProjectName("");
    setNewProjectDescription("");
    setIsCreateDialogOpen(false);
  };

  const handleEditProject = (project: BuildingProject) => {
    setEditingProject(project);
    setNewProjectName(project.name);
    setNewProjectDescription(project.description || "");
    setIsEditDialogOpen(true);
  };

  const handleUpdateProject = () => {
    if (!editingProject || !newProjectName.trim()) return;

    updateProject({
      id: editingProject.id,
      name: newProjectName.trim(),
      description: newProjectDescription.trim() || undefined,
    });

    setNewProjectName("");
    setNewProjectDescription("");
    setEditingProject(null);
    setIsEditDialogOpen(false);
  };

  const sortedProjects = useMemo(() => {
    const list = [...projects];
    // For modified date, fall back to created_at when modified_at is missing.
    // For user, prefer the modifier name when available, else the creator's name.
    const modifiedTime = (p: BuildingProject) => new Date(p.modified_at || p.created_at).getTime();
    const userName = (p: BuildingProject) => p.modified_by_name ?? p.created_by_name ?? "";
    switch (sortOption) {
      case "date-asc":
        return list.sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      case "name-asc":
        return list.sort((a, b) => a.name.localeCompare(b.name));
      case "name-desc":
        return list.sort((a, b) => b.name.localeCompare(a.name));
      case "modified-desc":
        return list.sort((a, b) => modifiedTime(b) - modifiedTime(a));
      case "modified-asc":
        return list.sort((a, b) => modifiedTime(a) - modifiedTime(b));
      case "user-asc":
        return list.sort((a, b) => userName(a).localeCompare(userName(b)));
      case "user-desc":
        return list.sort((a, b) => userName(b).localeCompare(userName(a)));
      case "date-desc":
      default:
        return list.sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
    }
  }, [projects, sortOption]);

  // "What is happening in this project" — only the building dashboard opts in.
  const renderActivity = (projectId: string | number) =>
    showActivity && (activityLoaded || activityError) ? (
      <ProjectActivityLine
        activity={activity?.get(Number(projectId))}
        isError={activityError}
        className="mt-auto pt-2"
      />
    ) : null;

  return (
    <div className="space-y-4">
      <div
        className={
          projects.length === 0
            ? ""
            : "grid grid-cols-1 gap-3 pb-2 pt-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6"
        }
      >
        {/* Create New Project Button — hidden for subcontractors and other users without create permission */}
        {canCreateProjects && (
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <button
                type="button"
                className={cn(
                  PROJECT_TILE_DASHED_CLASS,
                  "w-full",
                  projects.length === 0 ? "min-h-[200px]" : "min-h-[120px]"
                )}
              >
                <Plus className={projects.length === 0 ? "h-10 w-10" : "h-8 w-8"} />
                <span
                  className={cn("font-medium", projects.length === 0 ? "text-base" : "text-sm")}
                >
                  {t.projects.create.button}
                </span>
              </button>
            </DialogTrigger>
            <DialogContent
              className="max-h-[calc(100dvh-2rem)] max-w-2xl gap-0 overflow-y-auto p-0"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <DialogHeader className="px-6 pb-6 pt-6">
                <DialogTitle>{t.projects.create.modalTitle}</DialogTitle>
                <DialogDescription>{t.projects.create.modalSubtitle}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 px-6 pb-6">
                <div className="space-y-1.5">
                  <ProjectCreateFieldLabel
                    htmlFor={createNameId}
                    label={t.projects.create.modalNameLabel}
                  />
                  <Input
                    id={createNameId}
                    aria-required="true"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    placeholder={t.projects.create.modalNamePlaceholder}
                  />
                </div>
                <div className="space-y-1.5">
                  <ProjectCreateFieldLabel
                    htmlFor={createDescriptionId}
                    label={t.projects.create.modalDescriptionLabel}
                    optionalHint={t.projects.create.modalOptionalLabel}
                  />
                  <Textarea
                    id={createDescriptionId}
                    value={newProjectDescription}
                    onChange={(e) => setNewProjectDescription(e.target.value)}
                    placeholder={t.projects.create.modalDescriptionPlaceholder}
                    rows={3}
                  />
                </div>
                <ProjectStructureGuidance>
                  {t.projects.create.modalGuidance}
                </ProjectStructureGuidance>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    {t.common.cancel}
                  </Button>
                  <Button
                    onClick={handleCreateProject}
                    disabled={!newProjectName.trim() || isCreating}
                  >
                    {isCreating ? t.projects.create.creating : t.projects.create.modalSubmit}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Edit Project Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>{t.projects.edit.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-project-name">{t.projects.create.nameLabel}</Label>
                <Input
                  id="edit-project-name"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder={t.projects.create.namePlaceholder}
                />
              </div>
              <div>
                <Label htmlFor="edit-project-description">
                  {t.projects.create.descriptionLabel}
                </Label>
                <Textarea
                  id="edit-project-description"
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                  placeholder={t.projects.create.descriptionPlaceholder}
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  {t.common.cancel}
                </Button>
                <Button
                  onClick={handleUpdateProject}
                  disabled={!newProjectName.trim() || isUpdating}
                >
                  {isUpdating ? t.projects.edit.updating : t.projects.edit.submit}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Existing Projects */}
        {sortedProjects.map((project) => (
          <div
            key={project.id}
            className="group relative w-full cursor-pointer"
            onClick={() => onProjectSelect(project)}
          >
            {/* Same white card tile the dashboard grid uses. */}
            <div
              className={cn(
                PROJECT_TILE_CLASS,
                "relative h-full min-h-[120px] p-4",
                selectedProject?.id === project.id
                  ? "bg-primary/5 ring-1 ring-primary/30"
                  : PROJECT_TILE_INTERACTIVE_CLASS
              )}
            >
              {/* Header: icon + date */}
              <div className="mb-3 flex items-start justify-between">
                <FolderOpen
                  className={`h-6 w-6 transition-colors ${
                    selectedProject?.id === project.id ? "text-primary" : "text-primary/70"
                  }`}
                />
                <span className="text-xs text-muted-foreground">
                  {/* Day-first ("18 May" / "18. mai"), like every other project
                      card — the browser default renders month-first for en-US. */}
                  {new Date(project.created_at).toLocaleDateString(
                    language === "et" ? "et-EE" : "en-GB",
                    { day: "numeric", month: "short" }
                  )}
                </span>
              </div>

              {/* Title: left-aligned, multi-line with truncation */}
              <h3 className="line-clamp-3 text-sm font-semibold leading-snug">{project.name}</h3>
              {project.description && (
                <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                  {project.description}
                </p>
              )}

              {renderActivity(project.id)}

              {/* Edit button — gated to org_owner (and any role granted MODIFY_PROJECTS).
                  Subcontractors lack the perm and don't see the pencil. */}
              {canModifyProjects && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute bottom-1 right-1 h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditProject(project);
                  }}
                >
                  <Edit3 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
